import { ref, onBeforeUnmount } from 'vue';

interface UseDynamicIntervalOptions {
  intervalAlgorithm?: (currentInterval: number) => number;
  maxInitialInterval?: number;
  immediate?: boolean;
}

/**
 * useDynamicInterval composable
 * @param callback - 要执行的回调函数
 * @param initialInterval - 初始时间间隔，默认 1000 毫秒
 * @param options - 可选配置项
 */
export function useDynamicInterval(
  callback: () => void,
  initialInterval: number = 1000,
  options: UseDynamicIntervalOptions = {}
) {
  const { intervalAlgorithm, maxInitialInterval, immediate } = options;
  const interval = ref<number>(initialInterval);
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = () => {
    callback();

    interval.value = intervalAlgorithm
      ? Math.min(intervalAlgorithm(interval.value), maxInitialInterval || Infinity)
      : interval.value;

    timer = setTimeout(tick, interval.value);
  };

  const start = () => {
    if (!timer) {
      immediate && callback();
      timer = setTimeout(tick, interval.value);
    }
  };

  const stop = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  onBeforeUnmount(() => {
    stop();
  });

  return { start, stop, interval };
}
